import React from 'react';
import { KOREA_PATH, KOREA_VIEWBOX, project } from './koreaMap';

/**
 * 방문 컨설팅·설치를 **실제로 수행한** 곳.
 *
 * 설치 가능 지역이 아니라 수행 실적이다. 지역과 상관없이 전국을 다닌다는 것을 보여 주고,
 * 그러니 어디서든 부담 없이 미팅을 요청해도 된다는 뜻이다.
 *
 * 서울·경기는 당연히 많아서 개별 지명 대신 영역으로 묶는다. 지도의 주인공은 지방이다 —
 * 수도권 밖에서도 고르게 수행한다는 것이 이 지도가 하려는 말이다. 수도권 점들은 서로
 * 10~20px 거리라 이름을 다 붙이면 겹쳐서 읽을 수가 없기도 하다.
 *
 * 윤곽은 Natural Earth(퍼블릭 도메인)에서 만든 실제 남한 지형이다(koreaMap.js).
 * 점은 경위도로 찍는다 — 화면 좌표를 손으로 맞추면 지도를 다시 만들 때 전부 어긋난다.
 */

// [이름, 경도, 위도, 이름 위치(r 오른쪽 · l 왼쪽 · b 아래)]
export const OUTSIDE_REGIONS = [
    ['인천', 126.7052, 37.4563, 'l'],
    ['원주', 127.9202, 37.3422, 'r'],
    ['정선', 128.6608, 37.3807, 'b'],
    ['삼척', 129.1652, 37.4499, 'r'],
    ['대전', 127.3845, 36.3504, 'r'],
    ['전주', 127.148, 35.8242, 'r'],
    ['대구', 128.6014, 35.8714, 'r'],
    ['울산', 129.3114, 35.5384, 'r'],
    ['부산', 129.0756, 35.1796, 'r'],
    ['사천', 128.0642, 35.0036, 'l'],
];

/** 서울·경기 — 이름 없이 점만. 영역 표시로 묶는다. */
const METRO = [
    ['서울', 126.978, 37.5665],
    ['시흥', 126.803, 37.38],
    ['성남(판교)', 127.1112, 37.3947],
    ['수원', 127.0286, 37.2636],
    ['평택', 127.1128, 36.9921],
    ['포천', 127.2003, 37.8949],
];

/** 해외 수행지 — 지도 범위 밖이라 따로 적는다. */
export const OVERSEAS = ['베트남'];

const Label = ({ x, y, side, children }) => {
    const pos = {
        r: { x: x + 9, y: y + 4.5, anchor: 'start' },
        l: { x: x - 9, y: y + 4.5, anchor: 'end' },
        b: { x, y: y + 19, anchor: 'middle' },
    }[side];
    return (
        <text
            x={pos.x}
            y={pos.y}
            textAnchor={pos.anchor}
            // 지도 좌표계 기준 크기다. viewBox 와 함께 늘고 줄어 화면 폭이 바뀌어도 비율이 유지된다.
            fontSize="13"
            fontWeight="600"
            className="fill-gray-700"
        >
            {children}
        </text>
    );
};

const VisitMap = () => {
    const metro = METRO.map(([name, lon, lat]) => [name, ...project(lon, lat)]);
    const xs = metro.map(([, x]) => x);
    const ys = metro.map(([, , y]) => y);
    // 수도권 점들을 감싸는 영역. 여백을 둬서 점이 경계에 걸리지 않게 한다.
    const box = {
        cx: (Math.min(...xs) + Math.max(...xs)) / 2,
        cy: (Math.min(...ys) + Math.max(...ys)) / 2,
        rx: (Math.max(...xs) - Math.min(...xs)) / 2 + 16,
        ry: (Math.max(...ys) - Math.min(...ys)) / 2 + 16,
    };

    const regions = [...OUTSIDE_REGIONS.map(([n]) => n), '서울·경기', ...OVERSEAS.map((n) => `해외 ${n}`)];

    return (
        <figure className="m-0">
            <svg
                viewBox={KOREA_VIEWBOX}
                className="w-full h-auto max-w-[380px] mx-auto block"
                role="img"
                aria-label={`방문 컨설팅·설치를 수행한 지역: ${regions.join(', ')}`}
            >
                <path d={KOREA_PATH} className="fill-gray-100 stroke-gray-300" strokeWidth="1" strokeLinejoin="round" />

                {/* 서울·경기 — 영역으로 묶는다 */}
                <ellipse cx={box.cx} cy={box.cy} rx={box.rx} ry={box.ry} className="fill-primary-500" opacity="0.08" />
                <ellipse
                    cx={box.cx}
                    cy={box.cy}
                    rx={box.rx}
                    ry={box.ry}
                    className="fill-none stroke-primary-500"
                    strokeWidth="1"
                    strokeDasharray="3 3"
                    opacity="0.45"
                />
                {metro.map(([name, x, y]) => (
                    <circle key={name} cx={x} cy={y} r="4" className="fill-white stroke-primary-500" strokeWidth="2">
                        <title>{name}</title>
                    </circle>
                ))}
                <text
                    x={box.cx + box.rx + 5}
                    y={box.cy - box.ry + 14}
                    fontSize="12"
                    fontWeight="700"
                    className="fill-primary-700"
                >
                    서울·경기
                </text>

                {/* 서울·경기 밖 — 지명을 하나하나 */}
                {OUTSIDE_REGIONS.map(([name, lon, lat, side]) => {
                    const [x, y] = project(lon, lat);
                    return (
                        <g key={name}>
                            <circle cx={x} cy={y} r="9" className="fill-primary-500" opacity="0.18" />
                            <circle cx={x} cy={y} r="5" className="fill-primary-500" />
                            <Label x={x} y={y} side={side}>{name}</Label>
                        </g>
                    );
                })}
            </svg>

            <figcaption className="mt-4 text-center">
                <span className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-primary-500" aria-hidden="true" />
                        서울·경기 밖
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-primary-500" aria-hidden="true" />
                        서울·경기 (다수)
                    </span>
                </span>
                <span className="block mt-2 text-xs text-gray-500">
                    점은 실제로 방문 컨설팅·설치를 한 곳입니다.
                </span>
            </figcaption>
        </figure>
    );
};

export default VisitMap;
